import {
  defineComponent,
  ref,
  watch,
  onMounted,
  onUnmounted,
  nextTick,
} from "vue";
import { Category } from "@app/types/index";

export type CategoryListProps = {
  categories: Category[];
  activeCategory: number | null;
  onCategorySelect: (categoryId: number) => void;
};

export const CategoryList = defineComponent<CategoryListProps>({
  name: "CategoryList",
  props: ["categories", "activeCategory", "onCategorySelect"],
  setup(props) {
    const indicatorRef = ref<HTMLDivElement | null>(null);
    const headerRef = ref<HTMLDivElement | null>(null);
    const containerRef = ref<HTMLDivElement | null>(null);

    const updateIndicator = (categoryId: number | null) => {
      if (
        !categoryId ||
        !headerRef.value ||
        !indicatorRef.value ||
        !containerRef.value
      )
        return;

      const categoryEl = headerRef.value.querySelector(
        `[data-header-category="${categoryId}"]`,
      ) as HTMLElement;

      if (categoryEl) {
        const containerRect = containerRef.value.getBoundingClientRect();
        const categoryRect = categoryEl.getBoundingClientRect();

        const offsetLeft =
          categoryRect.left -
          containerRect.left +
          containerRef.value.scrollLeft;

        // Update indicator styles
        indicatorRef.value.style.transform = `translateX(${offsetLeft}px)`;
        indicatorRef.value.style.width = `${categoryRect.width}px`;
      }
    };

    const scrollCategoryIntoView = (categoryId: number | null) => {
      if (!categoryId || !headerRef.value || !containerRef.value) return;

      const categoryEl = headerRef.value.querySelector(
        `[data-header-category="${categoryId}"]`,
      ) as HTMLElement;

      if (categoryEl) {
        const containerRect = containerRef.value.getBoundingClientRect();
        const categoryRect = categoryEl.getBoundingClientRect();

        const scrollLeft =
          categoryRect.left -
          containerRect.left +
          containerRef.value.scrollLeft -
          (containerRect.width - categoryRect.width) / 2;

        containerRef.value.scrollTo({
          left: scrollLeft,
          behavior: "smooth",
        });
      }
    };

    const scrollToCategory = (categoryId: number) => {
      props.onCategorySelect(categoryId);
    };

    watch(
      () => props.activeCategory,
      async (categoryId) => {
        await nextTick();
        requestAnimationFrame(() => {
          scrollCategoryIntoView(categoryId);
          updateIndicator(categoryId);
        });
      },
      { immediate: true },
    );

    const handleScroll = () => {
      // Keep indicator in sync when user scrolls manually
      requestAnimationFrame(() => {
        updateIndicator(props.activeCategory);
      });
    };

    onMounted(() => {
      containerRef.value?.addEventListener("scroll", handleScroll, {
        passive: true,
      });
    });

    onUnmounted(() => {
      containerRef.value?.removeEventListener("scroll", handleScroll);
    });

    return () => (
      <div
        ref={containerRef}
        style="scrollbar-width: none;"
        class="relative overflow-x-auto flex-1"
      >
        <div ref={headerRef} class="relative flex py-1 px-0 min-w-max">
          <div
            ref={indicatorRef}
            class="absolute bottom-0 h-1 bg-[#047aff] transition-all duration-300 ease-in-out"
            style="transform: translateX(0); width: 0;"
          />

          {props.categories.map((category) => {
            const isActive = props.activeCategory === category.id;
            return (
              <button
                key={category.id}
                data-header-category={category.id}
                onClick={() => scrollToCategory(category.id)}
                class={
                  "whitespace-nowrap px-5 py-1 " +
                  (isActive ? " text-accent " : "")
                }
              >
                {category.name}
              </button>
            );
          })}
        </div>
      </div>
    );
  },
});
